import { RefObject, useEffect, useState } from "react";

type Props = {
    detailsRef: RefObject<HTMLDivElement | null>;
    specsRef: RefObject<HTMLDivElement | null>;
};

function scrollToSection(section: RefObject<HTMLDivElement | null>) {
    section?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
    });
}

export default function DetailsTabs({ detailsRef, specsRef }: Props) {
    const [activeSection, setActiveSection] = useState<"details" | "specs">("details");

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (entry.target === detailsRef.current) setActiveSection("details");
                        if (entry.target === specsRef.current) setActiveSection("specs");
                    }
                });
            },
            {
                threshold: 0,
                rootMargin: "-40% 0px -60% 0px" // triggers when section enters top 40% of screen
            }
        );

        if (detailsRef.current) observer.observe(detailsRef.current);
        if (specsRef.current) observer.observe(specsRef.current);

        return () => observer.disconnect();
    }, [detailsRef, specsRef]);

    const activeClass = "font-semibold border-b-2 border-black -mb-4 pb-4 text-black";
    const inactiveClass = "text-gray-500 hover:text-black";

    return (
        <div className="sticky top-0 z-50 bg-white flex gap-10 border-b-2 border-black pb-4 pt-3 text-lg">
            <button
                className={activeSection === "details" ? activeClass : inactiveClass}
                onClick={() => scrollToSection(detailsRef)}
            >
                Description
            </button>
            <button
                className={activeSection === "specs" ? activeClass : inactiveClass}
                onClick={() => scrollToSection(specsRef)}
            >
                Specifications
            </button>
            <button className={inactiveClass}>Prices</button>
            <button className={inactiveClass}>Reviews</button>
        </div>
    );
}